import {
  PROJECT_CARD_ASPECT_RATIO,
  PROJECT_CARD_THUMB_2X_HEIGHT,
  PROJECT_CARD_THUMB_2X_WIDTH,
} from './projectCoverAspect';

/** Brand-tinted generated audio card colours — keep in sync with media `thumbs.php`. */
export const AUDIO_CARD_BACKGROUND = '#e8f2fc';
export const AUDIO_CARD_BAR = '#0777DA';
export const AUDIO_CARD_TITLE = '#3d6a8f';

/**
 * Relative bar heights for the stylised waveform (0–1).
 * Mirrored in `Clowdy\\Services\\Thumbnailer\\Clowd\\Audio\\Audio`.
 */
export const AUDIO_CARD_WAVEFORM_HEIGHTS = [
  0.35, 0.55, 0.8, 0.45, 1.0, 0.6, 0.9, 0.4, 0.7, 0.5, 0.85, 0.55,
];

/**
 * Format a project title for the baked-in audio card line.
 *
 * @param {string} title
 * @returns {string}
 */
export function formatAudioCardTitle(title) {
  const label = String(title || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

  if (!label) {
    return 'UNTITLED PROJECT';
  }

  if (label.length > 42) {
    return `${label.slice(0, 41).trim()}…`;
  }

  return label;
}

/**
 * Paint a generated audio card onto a canvas (16:9, brand tint + waveform + title).
 *
 * @param {string} title
 * @param {{ width?: number, height?: number }} [options]
 * @returns {string|null} data URL, or null when canvas APIs are unavailable
 */
export function generateAudioCardDataUrl(
  title,
  {
    width = PROJECT_CARD_THUMB_2X_WIDTH,
    height = PROJECT_CARD_THUMB_2X_HEIGHT,
  } = {}
) {
  if (typeof document === 'undefined') {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  ctx.fillStyle = AUDIO_CARD_BACKGROUND;
  ctx.fillRect(0, 0, width, height);

  drawWaveform(ctx, width, height);
  drawTitle(ctx, title, width, height);

  try {
    return canvas.toDataURL('image/jpeg', 0.92);
  } catch (err) {
    return null;
  }
}

/**
 * Static slim waveform bars for the audio file-row preview (no seek).
 *
 * @returns {number[]} heights 0–1
 */
export function getAudioFileRowWaveformHeights() {
  // Ten slim bars of varying height for the inline file-row motif.
  return [0.4, 0.7, 0.45, 0.9, 0.55, 1, 0.35, 0.8, 0.5, 0.65];
}

function drawWaveform(ctx, width, height) {
  const heights = AUDIO_CARD_WAVEFORM_HEIGHTS;
  const barCount = heights.length;
  const barWidth = Math.max(4, Math.round(width * 0.018));
  const gap = Math.max(4, Math.round(barWidth * 0.85));
  const maxBarHeight = Math.round(height * 0.38);
  const totalWidth = barCount * barWidth + (barCount - 1) * gap;
  const startX = Math.round((width - totalWidth) / 2);
  const centreY = Math.round(height * 0.42);

  ctx.fillStyle = AUDIO_CARD_BAR;
  heights.forEach((ratio, index) => {
    const barHeight = Math.max(6, Math.round(maxBarHeight * ratio));
    const x = startX + index * (barWidth + gap);
    const y = centreY - Math.round(barHeight / 2);
    ctx.fillRect(x, y, barWidth, barHeight);
  });
}

function drawTitle(ctx, title, width, height) {
  const label = formatAudioCardTitle(title);
  let fontSize = Math.max(14, Math.round(height * 0.055));
  const maxTextWidth = Math.round(width * 0.82);
  const y = Math.round(height * 0.78);

  ctx.fillStyle = AUDIO_CARD_TITLE;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${fontSize}px "Plus Jakarta Sans", "Helvetica Neue", Arial, sans-serif`;

  while (fontSize > 10 && ctx.measureText(label).width > maxTextWidth) {
    fontSize -= 1;
    ctx.font = `700 ${fontSize}px "Plus Jakarta Sans", "Helvetica Neue", Arial, sans-serif`;
  }

  // Approximate small-caps via uppercase (letter-spacing is not widely supported on canvas).
  ctx.fillText(label, Math.round(width / 2), y);
}

/** Generated audio cards match image/video portfolio framing (16:9). */
export const AUDIO_CARD_ASPECT_RATIO = PROJECT_CARD_ASPECT_RATIO;
